/**
 * CU-14zefhy2ydj C2 (web): which model tier answers a question. The user picks Auto (the router decides), Fast or Thorough; the
 * choice goes with each message as `tierPreference` (J-5, absent = auto) and the reply shows who answered (`answeredBy`, J-6).
 * The web never sends a model id. Pure helpers (no Angular), so they run under node in the spec.
 */

export type TierPreference = 'auto' | 'fast' | 'thorough';

export interface TierOption {
    value: TierPreference;
    label: string;
    hint: string;
    /** false = cannot be picked now (see `tierOptionsFor`) */
    enabled: boolean;
}

/** localStorage key base (scoped to the person; it starts with the chat storage prefix, so logout clears it with the chats) */
export const TIER_KEY_BASE = 'hm-assistant-state-v2-tier';

const HINTS: {[tier in TierPreference]: string} = {
    auto: 'The assistant picks the model for each question',
    fast: 'Quick answers for simple questions (first question of a chat only)',
    thorough: 'Slower, more careful answers for multi-part questions'
};
const LABELS: {[tier in TierPreference]: string} = {auto: 'Auto', fast: 'Fast', thorough: 'Thorough'};

/** A stored or typed value as a preference; anything unknown is `auto`. */
export function normalizeTier(value: any): TierPreference {
    return value === 'fast' || value === 'thorough' ? value : 'auto';
}

/**
 * The options of the switch. The router never switches down, so Fast is offered only before the chat's first question
 * (`chatStarted` = the chat already has a user message).
 */
export function tierOptionsFor(chatStarted: boolean): TierOption[] {
    return (['auto', 'fast', 'thorough'] as TierPreference[]).map((value) => ({
        value,
        label: LABELS[value],
        hint: HINTS[value],
        enabled: !(value === 'fast' && chatStarted)
    }));
}

/** The preference that goes with a message: a chosen Fast no longer applies once the chat has started. */
export function effectiveTier(preference: TierPreference, chatStarted: boolean): TierPreference {
    return preference === 'fast' && chatStarted ? 'auto' : normalizeTier(preference);
}

/** The `tierPreference` request field: absent (undefined) for auto, so old servers and the default path are unchanged. */
export function tierField(preference: TierPreference): TierPreference | undefined {
    const tier = normalizeTier(preference);
    return tier === 'auto' ? undefined : tier;
}

/** "Try harder" on a retry always asks for the thorough tier, whatever the switch says. */
export const TRY_HARDER_TIER: TierPreference = 'thorough';

type StorageLike = {getItem(key: string): string | null; setItem(key: string, value: string): void} | null | undefined;

const keyOf = (scope: string): string | null => (scope ? `${TIER_KEY_BASE}:${scope}` : null);

/** The person's saved preference; `auto` when none, no storage or an unknown person. */
export function readTier(storage: StorageLike, scope: string): TierPreference {
    const key = keyOf(scope);
    if (!storage || !key) {
        return 'auto';
    }
    try {
        return normalizeTier(storage.getItem(key));
    } catch (e) {
        return 'auto';
    }
}

/** Saves the preference for this person; false when it could not be stored. */
export function saveTier(storage: StorageLike, scope: string, preference: TierPreference): boolean {
    const key = keyOf(scope);
    if (!storage || !key) {
        return false;
    }
    try {
        storage.setItem(key, normalizeTier(preference));
        return true;
    } catch (e) {
        return false;
    }
}

/** Which model answered: the router's tier and model name (J-6). */
export interface AnsweredBy {
    tier?: 'fast' | 'deep';
    model?: string;
}

/** `answeredBy` from an events payload or a stored message; null when it names neither a tier nor a model. */
export function parseAnsweredBy(raw: any): AnsweredBy | null {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const tier = raw.tier === 'fast' || raw.tier === 'deep' ? raw.tier : undefined;
    const model = typeof raw.model === 'string' && /^[A-Za-z0-9._ -]{1,40}$/.test(raw.model.trim()) ? raw.model.trim() : undefined;
    if (!tier && !model) {
        return null;
    }
    const out: AnsweredBy = {};
    if (tier) {
        out.tier = tier;
    }
    if (model) {
        out.model = model;
    }
    return out;
}

/** "Answered by Sonnet · Fast" / "Answered by Opus · Thorough"; '' when unknown. */
export function answeredByLabel(answeredBy: AnsweredBy | null | undefined): string {
    const a = parseAnsweredBy(answeredBy);
    if (!a) {
        return '';
    }
    const model = a.model ? a.model.charAt(0).toUpperCase() + a.model.slice(1) : '';
    const tier = a.tier === 'fast' ? LABELS.fast : a.tier === 'deep' ? LABELS.thorough : '';
    return ['Answered by ' + (model || 'the assistant'), tier].filter((p) => !!p).join(' · ');
}
